/**
 * apps/web :: components/paywall/TierCard
 *
 * Single tier card used by the paywall picker (ROADS-445 FE-01).
 *
 * Reusable so:
 *   - The full picker renders 4 cards side-by-side (Class C $20,
 *     CDL $49, Tourist $15, Outbound $15).
 *   - The renew-existing-tier path renders a SINGLE card with the
 *     pre-selected tier and a "Renew $X" CTA.
 *
 * Props are deliberately presentational — the Paywall parent owns the
 * decision logic. Each card invokes `onSelect` with its tier; the
 * parent then routes the user to /checkout (FE-02).
 */
import { useTranslation } from "react-i18next";
import type { EntitlementTier } from "@/lib/entitlement-api";

export interface TierCardProps {
  tier: EntitlementTier;
  /** Display name; usually i18n'd "Class C", "CDL", "Tourist", "Outbound". */
  name: string;
  /** Localised price string, e.g. "$20". */
  priceLabel: string;
  /** Localised cadence, e.g. "/state · 6 mo". Optional. */
  cadenceLabel?: string;
  /** One-line value prop, i18n'd. */
  description: string;
  /** When true, the card renders a "Renew" CTA (vs. "Add a card"). */
  isRenewal?: boolean;
  onSelect: (tier: EntitlementTier) => void;
}

export default function TierCard({
  tier,
  name,
  priceLabel,
  cadenceLabel,
  description,
  isRenewal = false,
  onSelect,
}: TierCardProps): JSX.Element {
  const { t } = useTranslation();
  const cta = isRenewal
    ? t("paywall.tier.renewCta", {
        priceLabel,
        defaultValue: "Renew {{priceLabel}}",
      })
    : t("paywall.tier.selectCta", {
        priceLabel,
        defaultValue: "Choose {{priceLabel}}",
      });
  return (
    <div
      data-testid={`paywall-tier-card-${tier}`}
      className="flex flex-col gap-sm rounded-2xl border border-slate-200 bg-white p-md shadow-sm focus-within:ring-2 focus-within:ring-teal-500"
    >
      <div className="flex items-baseline justify-between gap-sm">
        <h3 className="text-base font-semibold text-slate-900">{name}</h3>
        <div className="flex items-baseline gap-1 text-slate-900">
          <span className="text-2xl font-bold">{priceLabel}</span>
          {cadenceLabel ? (
            <span className="text-xs text-slate-500">{cadenceLabel}</span>
          ) : null}
        </div>
      </div>
      <p className="text-sm text-slate-600 flex-1">{description}</p>
      <button
        type="button"
        data-testid={`paywall-tier-cta-${tier}`}
        onClick={() => onSelect(tier)}
        className="mt-sm inline-flex h-11 items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
      >
        {cta}
      </button>
    </div>
  );
}
